/// <reference types="@angular/localize" />

import {provideHttpClient, withInterceptorsFromDi} from '@angular/common/http';
import {
  enableProdMode,
  ErrorHandler,
  importProvidersFrom,
  inject,
  LOCALE_ID,
  provideAppInitializer,
} from '@angular/core';
import {bootstrapApplication, BrowserModule} from '@angular/platform-browser';
import {provideRouter, Router} from '@angular/router';

import * as Sentry from '@sentry/angular';
import {
  browserProfilingIntegration,
  browserTracingIntegration,
  createErrorHandler,
  TraceService,
} from '@sentry/angular';
import {KeycloakAngularModule, KeycloakService} from 'keycloak-angular';
import {ThemeService} from 'ng2-charts';
import {AppComponent} from './app/app.component';
import {routes} from './app/app.routes';

import {environment} from './environments/environment';

Sentry.init({
  enabled: !!environment.sentry.dsn,
  integrations: [browserTracingIntegration(), browserProfilingIntegration()],
  dsn: environment.sentry.dsn,
  environment: environment.sentry.environment,
  release: environment.sentry.release,
  tracesSampleRate: environment.sentry.tracesSampleRate,
  tracePropagationTargets: ['http://localhost:3000/api', 'https://fit.uni-kassel.de/api'],
  profilesSampleRate: environment.sentry.profilesSampleRate,
  beforeSend: async (event, hint) => {
    function anonymize(message: string) {
      return message
        .replaceAll(/[a-f0-9]{24}/g, '<objectid>')
        .replaceAll(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/g, '<uuid>');
    }

    if (event.message) {
      event.message = anonymize(event.message);
    }
    for (const exception of event.exception?.values ?? []) {
      if (exception.value) {
        exception.value = anonymize(exception.value);
      }
    }
    const exception = hint.originalException;
    if (typeof exception === 'string' && exception.startsWith('Http failure response for ')) {
      const match = exception.match(/^Http failure response for (.+): (.+)$/);
      if (match) {
        const [, url, message] = match;
        event.fingerprint = [anonymize(url), message];
      }
    }
    hint.attachments = [
      {
        filename: "screenshot.jpg",
        data: await captureScreen(),
        contentType: 'image/jpeg',
      },
    ];
    return event;
  },
});

async function captureScreen(): Promise<Uint8Array> {
  const html2canvas = await import('html2canvas');
  const canvas = await html2canvas.default(document.body, {
    scale: 1,
    scrollY: -window.scrollY,
    logging: false,
    ignoreElements: element => element instanceof SVGElement,
  });
  const blob: Blob = await new Promise((resolve, reject) => canvas.toBlob(
    blob => blob ? resolve(blob) : reject(),
    'image/jpeg',
    0.5, // go for lower quality. A 4k screenshot is about 100KiB this way and text is still readable.
  ));
  const buffer = await blob.arrayBuffer();
  return new Uint8Array(buffer);
}

if (environment.production) {
  enableProdMode();
}

bootstrapApplication(AppComponent, {
  providers: [
    importProvidersFrom(
      BrowserModule,
    ),
    provideRouter(routes),
    provideAppInitializer(() => {
      return inject(KeycloakService).init({
        config: environment.auth,
        initOptions: {
          onLoad: 'check-sso',
          silentCheckSsoRedirectUri: window.location.origin + '/assets/silent-check-sso.html',
        },
        enableBearerInterceptor: true,
        loadUserProfileAtStartUp: true,
      }).catch(err => console.error('Keycloak init failed', err));
    }),
    importProvidersFrom(KeycloakAngularModule),
    provideHttpClient(withInterceptorsFromDi()),
    ThemeService,
    {
      provide: ErrorHandler,
      useValue: createErrorHandler(),
    },
    {
      provide: TraceService,
      deps: [Router],
    },
  ],
})
  .catch(err => console.error(err));
